import { UA } from './../uas/ua.interface';
import { Upa, UaEditRead } from './../uas/ua.model';
import { UaTranslation } from './../uas/ua-building-blocks';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import * as _ from 'lodash';
import { map, throwError, Observable } from 'rxjs';

export enum UaType {
  UPA,
  UTA,
}

@Injectable({ providedIn: 'root' })
export abstract class AbstractUaService {
  path: string = '/api';

  // invariant: subclasses (UpaService/UtaService) call `init()` in their constructor.
  subpath!: string;
  subpathSingular!: string;

  constructor(protected apiService: ApiService) { }

  protected init(type: UaType) {
    if (type === UaType.UPA) {
      this.subpath = 'upas';
      this.subpathSingular = 'upa';
    } else if (type === UaType.UTA) {
      this.subpath = 'utas';
      this.subpathSingular = 'uta';
    }
  }

  // GET /api/upas/:id (Resources::Upa) / /api/utas/:id (Resources::Uta) — the saved UA loaded into
  // the generic editor: content + server-computed edit action state. Both variety serializers satisfy
  // `UaEditRead` (its fields are the UPA∩UTA intersection the editor reads).
  getUa(id: number, airlineId: number): Observable<UaEditRead> {
    return this.apiService.get<UaEditRead>(`${this.path}/${this.subpath}/${id}?airline_id=${airlineId}`);
  }

  getUpa(id: number, airlineId: number): Observable<Upa> {
    return this.apiService.get<{ data: Upa }>(`${this.path}/v2/airlines/${airlineId}/upas/${id}`)
      .pipe(map(response => response.data));
  }

  getUaChangeLog<T = unknown>(id: number, airlineId: number): Observable<T> {
    return this.apiService.get<T>(`${this.path}/v2/airlines/${airlineId}/upa_change_logs/${id}`);
  }

  enableUa(id: number) {
    return this.apiService.post(`${this.path}/${this.subpath}/${id}/enable`, {});
  }

  disableUa(id: number) {
    return this.apiService.post(`${this.path}/${this.subpath}/${id}/disable`, {});
  }

  submitUa(id: number) {
    return this.apiService.post(`${this.path}/${this.subpath}/${id}/submit`, {});
  }

  publishUa(id: number) {
    return this.apiService.post(`${this.path}/${this.subpath}/${id}/publish`, {});
  }

  updateUaStatus(id: number, data: any) {
    return this.apiService
      .post(`${this.path}/${this.subpath}/${id}/status_transitions`, { data });
  }

  updateUa(id: number) {
    return this.apiService.put(`${this.path}/${this.subpath}/${id}`, {});
  }

  updateUpas(partnerId: any, data: {upa_ids: number[], status?: string}) {
    return this.apiService.put(`${this.path}/v2/airlines/${partnerId}/upas`, data);
  }

  deleteUa(id: number) {
    return this.apiService.delete(`${this.path}/${this.subpath}/${id}`);
  }

  deleteUpas(upaIds: number[], partnerId: any) {
    return this.apiService.deleteBody(`${this.path}/v2/airlines/${partnerId}/upas`, { upa_ids: upaIds });
  }

  // UPA clone returns only { id } (requests/airlines/upas/create.rb) — not a full UA.
  cloneUa(id: number, partnerId: any) {
    return this.apiService.post<{ id: number }>(`${this.path}/airlines/${partnerId}/${this.subpath}`, { data: {source_upa_id: id,}})
  }

  getUaSession(id: number) {
    return this.apiService
      .get(`${this.path}/${this.subpath}/${id}/session_${this.subpathSingular}`);
  }

  createUaSession(id: number, data: Partial<UA>) {
    return this.apiService
      .post(`${this.path}/${this.subpath}/${id}/session_${this.subpathSingular}`, { data });
  }

  updateUaSession(id: number, data: Partial<UA>) {
    return this.apiService
      .put(`${this.path}/${this.subpath}/${id}/session_${this.subpathSingular}`, { data });
  }

  deleteUaSession(id: number) {
    return this.apiService
      .delete(`${this.path}/${this.subpath}/${id}/session_${this.subpathSingular}`);
  }

  // Enriches a UA's translations in place: fills missing locales with a blank default and stamps the
  // FE-only `translated` flag. Generic so it preserves the caller's shape (Upa / UaEditRead / …).
  processUaTranslations<T extends { translations: Partial<Record<string, UaTranslation & { translated?: boolean }>> }>(
    ua: T,
    languages: string[],
  ): T {
    const defaultTranslation = { headline: '', description: '', cta_text: '', cta_url: '' };
    languages.forEach((code: string) => {
      if (!ua.translations[code]) {
        const translation = _.clone(defaultTranslation) as UaTranslation & { translated?: boolean };
        translation.translated = false;
        ua.translations[code] = translation;
      }
    });
    return ua;
  }

  processUasTranslations(uas: UA[], languages: string[], defaultCode: string) {
    return uas.map((ua: UA) => {
      const defaultTranslation = _.clone(ua.translations[defaultCode]);
      languages.forEach((code: string, index: number) => {
        if (ua.translations[code].headline.length === 0) {
          ua.translations[code] = defaultTranslation;
          ua.translations[code].translated = false;
        } else {
          ua.translations[code].translated = true;
        }
      });
      return ua;
    });
  }
}
